import React, { useMemo, useState, useRef, useCallback } from "react";
import { DndContext, closestCenter, DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import { saveQuiz } from "../../api";
import { MergedWaypoint } from "../../types/types";
import { WaypointListProps } from "../../types/screensTypes";
import EditableWaypointCard from "../cards/EditableWaypointCard";
import WaypointCard from "../cards/WaypointCard";
import { useNotification } from "../../context/NotificationContext";
import i18n from "../../i18n";

/**
 * Generate a stable internalId for a waypoint that matches what the backend expects.
 * This ensures consistency between card saves and drag-and-drop saves.
 */
const getStableInternalId = (waypoint: MergedWaypoint): string => {
    // If waypoint already has an internalId (from a temporary waypoint), use that
    if (waypoint.internalId) {
        return waypoint.internalId;
    }
    // Otherwise, compute the same ID that the backend would create
    // Backend uses: `${waypointId}-temp-${languageForTemp}` where languageForTemp defaults to "iduniqueen"
    const waypointId = waypoint.waypointId || waypoint.id;
    return `${waypointId}-temp-iduniqueen`;
};

// Define props for SortableItem component
interface SortableItemProps {
    id: string;
    waypoint: MergedWaypoint;
    onClick: () => void;
    onDelete: () => void;
    isLoading?: boolean;
    refreshWaypoints?: () => void;
    areWaypointsEditable?: boolean;
    onSaveStart?: () => void;
    onSaveEnd?: () => void;
    onUnsavedChange?: () => void;
    isStop?: boolean;
}

// SortableItem Component (Draggable Waypoint Item)
const SortableItem: React.FC<SortableItemProps> = ({
    id,
    waypoint,
    onClick,
    onDelete,
    isLoading = false,
    refreshWaypoints,
    areWaypointsEditable = false,
    onSaveStart,
    onSaveEnd,
    onUnsavedChange,
    isStop = false,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        marginBottom: "10px", // Add spacing between items
    };

    const waypointType = waypoint.type as string;
    const position = waypointType === "STOP" ? waypoint.position - 1000 : waypoint.position;
    const backgroundColor = waypointType === "INVISIBLE" ? "#ddd" : "white";

    const displayName = useMemo(() => {
        // Normalize language code to uppercase to match API data
        const langCode = i18n.language?.toUpperCase();

        // Try to find a localized entry with matching language
        const localizedEntry = waypoint?.waypointLocalized?.find((loc) => loc.language?.toUpperCase() === langCode);

        // Use localized name if available, otherwise fallback to internalName
        return localizedEntry?.name?.trim() || waypoint?.internalName || "";
    }, [waypoint, i18n.language]);

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            {areWaypointsEditable ? (
                <EditableWaypointCard
                    name={displayName || (waypointType === "INVISIBLE" ? t("waypoint-invisible") : t("waypoint"))}
                    position={position}
                    onClick={() => {
                        if (waypointType !== "INVISIBLE") {
                            onClick();
                        }
                    }}
                    onDelete={onDelete}
                    dragHandleProps={listeners} // Pass drag listeners to WaypointCard
                    color={backgroundColor}
                    isLoading={isLoading}
                    waypoint={waypoint}
                    refreshWaypoints={refreshWaypoints}
                    onSaveStart={onSaveStart}
                    onSaveEnd={onSaveEnd}
                    onUnsavedChange={onUnsavedChange}
                />
            ) : (
                <WaypointCard
                    name={displayName || (waypointType === "INVISIBLE" ? t("waypoint-invisible") : t("waypoint"))}
                    position={position}
                    onClick={() => {
                        if (waypointType !== "INVISIBLE") {
                            onClick();
                        }
                    }}
                    onDelete={onDelete}
                    dragHandleProps={listeners} // Pass drag listeners to WaypointCard
                    color={backgroundColor}
                    isLoading={isLoading}
                    isStop={isStop}
                />
            )}
        </div>
    );
};

// WaypointList Component (Draggable List of Waypoints)
const WaypointList: React.FC<WaypointListProps> = ({
    combinedWaypoints,
    setCombinedWaypoints,
    saveTemporaryWaypoint,
    saveStops,
    navigateToNext,
    deleteWaypoint,
    isLoading,
    isStop = false,
    isQuiz = false,
    refreshWaypoints,
    areWaypointsEditable = false,
    onUnsavedChange,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { showNotification } = useNotification();

    // Track pending saves to prevent drag during saves
    const [isSaving, setIsSaving] = useState(false);
    const pendingSavesRef = useRef(0);

    const handleDragStart = (event: DragStartEvent) => {
        // Check if there are pending saves
        if (pendingSavesRef.current > 0) {
            showNotification({
                message: t("waitForSavesToComplete") || "Please wait for changes to save before reordering",
                type: "warning",
            });
        }
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const token = localStorage.getItem("token");
        if (!token) return;
        const { active, over } = event;

        // Exit if no destination or item didn't change position
        if (!over || active.id === over.id) return;

        // Prevent drag during saves to avoid race conditions
        if (pendingSavesRef.current > 0) {
            showNotification({
                message: t("waitForSavesToComplete") || "Please wait for changes to save before reordering",
                type: "warning",
            });
            return;
        }

        const oldIndex = combinedWaypoints.findIndex((waypoint) => waypoint.id === active.id);
        const newIndex = combinedWaypoints.findIndex((waypoint) => waypoint.id === over.id);

        // Reorder the waypoints
        const reorderedWaypoints = arrayMove(combinedWaypoints, oldIndex, newIndex);

        setIsSaving(true);

        // Update positions and create temporary waypoints if needed
        const updatedWaypoints = reorderedWaypoints.map((waypoint, index) => {
            const newPosition = isStop ? index + 1001 : index + 1; // Adjust position for stop waypoints

            if (isQuiz) {
                if (waypoint.position !== newPosition) {
                    const tempWaypoint = {
                        ...waypoint,
                        position: newPosition,
                    };

                    // Save updated quiz waypoint
                    saveQuiz({
                        token,
                        trackId: waypoint.trackId,
                        quizId: waypoint.id,
                        latitude: waypoint.latitude || 0,
                        longitude: waypoint.longitude || 0,
                        position: newPosition,
                    });

                    return tempWaypoint;
                }
                return waypoint;
            } else {
                const isTemp = Boolean(waypoint.status);

                if (waypoint.position !== newPosition) {
                    // Use stable internalId that matches what EditableWaypointCard uses
                    const stableId = getStableInternalId(waypoint);
                    const tempWaypoint = {
                        ...waypoint,
                        position: newPosition,
                        internalId: stableId,
                        waypointId: isTemp ? waypoint.waypointId : waypoint.id, // Link to original waypoint
                    };

                    // Save temporary waypoint
                    if (isStop && saveStops) {
                        saveStops({
                            token,
                            trackId: waypoint.trackId,
                            waypointId: tempWaypoint.waypointId || null,
                            latitude: waypoint.latitude || 0,
                            longitude: waypoint.longitude || 0,
                            position: newPosition,
                        });
                    } else if (!isStop && saveTemporaryWaypoint) {
                        saveTemporaryWaypoint({
                            token: localStorage.getItem("token") || "",
                            internalId: stableId,
                            trackId: waypoint.trackId,
                            waypointId: tempWaypoint.waypointId || null,
                            latitude: waypoint.latitude || 0,
                            longitude: waypoint.longitude || 0,
                            position: newPosition,
                        });
                    }

                    return tempWaypoint;
                }
                return waypoint;
            }
        });

        // Update the state immediately for responsiveness
        setCombinedWaypoints(updatedWaypoints);

        // Notify parent that there are unsaved changes (drag-and-drop creates temporary waypoints)
        onUnsavedChange?.();

        // Small delay to allow saves to process, then clear saving state
        setTimeout(() => {
            setIsSaving(false);
        }, 500);
    };

    // Callbacks to track pending saves from EditableWaypointCard
    // Used to prevent drag and drop during active saves
    const onSaveStart = useCallback(() => {
        pendingSavesRef.current += 1;
    }, []);

    const onSaveEnd = useCallback(() => {
        pendingSavesRef.current = Math.max(0, pendingSavesRef.current - 1);
    }, []);

    return (
        <DndContext collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <SortableContext items={combinedWaypoints.map((wpt) => wpt.id)} strategy={verticalListSortingStrategy}>
                <div className="waypoints-list">
                    {combinedWaypoints.map((waypoint) => (
                        <SortableItem
                            key={waypoint.id}
                            id={waypoint.id}
                            waypoint={waypoint}
                            onClick={() => navigateToNext(waypoint.id)}
                            onDelete={() => deleteWaypoint(waypoint.id)}
                            isLoading={isLoading || isSaving}
                            refreshWaypoints={refreshWaypoints}
                            areWaypointsEditable={areWaypointsEditable}
                            onSaveStart={onSaveStart}
                            onSaveEnd={onSaveEnd}
                            onUnsavedChange={onUnsavedChange}
                            isStop={isStop}
                        />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
};

export default WaypointList;
